import { useState, useMemo, useEffect, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { DiscardDialog } from "@/components/DiscardDialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X } from "lucide-react";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  createPointReward,
  updatePointReward,
  uploadStoreImage,
  getSettings,
} from "@/lib/api/admin";
import type { PointReward } from "@/lib/api/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TextInput } from "@/components/TextInput";
import { Textarea } from "@/components/Textarea";
import { Label } from "@/components/ui/label";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Spinner } from "@/components/Spinner";
import { ImageUpload } from "@/components/ImageUpload";
import { BYPASS_MAX_PRICE_DOLLARS } from "@/lib/constants";

// ─── Schema factory (price max depends on props) ──────────────────────────────

function makeSchema(maxPriceDollars: number) {
  return z.object({
    name: z
      .string()
      .min(2, "Name must be at least 2 characters.")
      .max(50, "Name must be 50 characters or fewer."),
    description: z.string().max(700, "Description must be 700 characters or fewer."),
    price: z
      .string()
      .refine((v) => !isNaN(parseFloat(v)) && parseFloat(v) >= 1, "Price must be at least $1.00.")
      .refine(
        (v) => parseFloat(v) <= maxPriceDollars,
        `Price cannot exceed $${maxPriceDollars.toLocaleString()}.`
      ),
    pointsRequired: z
      .string()
      .refine(
        (v) => !isNaN(parseInt(v, 10)) && parseInt(v, 10) >= 0,
        "Points required must be 0 or more."
      ),
    status: z.enum(["active", "inactive"]),
  });
}

type PointRewardFormValues = z.infer<ReturnType<typeof makeSchema>>;

// ─── Point Reward Form Dialog ─────────────────────────────────────────────────

export type RewardPrefill = {
  name: string;
  description?: string;
  priceCents: number;
  imageUrl?: string;
  imageFile?: File;
};

type PointRewardFormDialogProps = {
  open: boolean;
  onClose: () => void;
  storeId: string;
  priceLimitBypass: boolean;
  storeCategory: string;
  reward?: PointReward;
  prefill?: RewardPrefill;
};

export function PointRewardFormDialog({
  open,
  onClose,
  storeId,
  priceLimitBypass,
  storeCategory,
  reward,
  prefill,
}: PointRewardFormDialogProps) {
  const user = useAdminAuth();
  const qc = useQueryClient();
  const [imageFile, setImageFile] = useState<File | null>(prefill?.imageFile ?? null);
  const [imageFileDirty, setImageFileDirty] = useState(false);
  const [imageUrl, setImageUrl] = useState(
    reward?.image_url ?? (prefill?.imageFile ? undefined : prefill?.imageUrl)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [conditions, setConditions] = useState<string[]>(reward?.conditions ?? []);
  const [conditionInput, setConditionInput] = useState("");
  const [conditionError, setConditionError] = useState<string | null>(null);

  const { data: settings } = useQuery({
    queryKey: ["settings"],
    queryFn: () => getSettings(user!),
    enabled: !!user,
  });

  const maxPriceDollars = priceLimitBypass
    ? BYPASS_MAX_PRICE_DOLLARS
    : storeCategory === "nightclub"
      ? (settings?.nightclub_max_price_cents ?? 50000) / 100
      : (settings?.default_max_price_cents ?? 30000) / 100;

  const schema = useMemo(() => makeSchema(maxPriceDollars), [maxPriceDollars]);

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm<PointRewardFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: reward?.name ?? prefill?.name ?? "",
      description: reward?.description ?? prefill?.description ?? "",
      price: reward
        ? (reward.price_cents / 100).toFixed(2)
        : prefill
          ? (prefill.priceCents / 100).toFixed(2)
          : "",
      pointsRequired: reward
        ? String(reward.points_required)
        : prefill
          ? String(Math.floor((prefill.priceCents / 100) * 50))
          : "",
      status: reward?.status ?? "active",
    },
  });

  const priceValue = watch("price");
  const pointsManuallyEdited = useRef(!!reward);
  const priceField = register("price");
  const pointsRequiredField = register("pointsRequired");

  useEffect(() => {
    if (pointsManuallyEdited.current) return;
    const dollars = parseFloat(priceValue);
    if (!isNaN(dollars) && dollars >= 1) {
      setValue("pointsRequired", String(Math.floor(dollars * 50)));
    }
  }, [priceValue, setValue]);

  const conditionsDirty =
    JSON.stringify(conditions) !== JSON.stringify(reward?.conditions ?? []) ||
    conditionInput.trim() !== "";

  function tryClose() {
    if (isDirty || imageFileDirty || conditionsDirty) {
      setDiscardOpen(true);
    } else {
      onClose();
    }
  }

  async function onSubmit(values: PointRewardFormValues) {
    // Auto-save any pending condition text using the same rule as the Add button.
    const trimmedCondition = conditionInput.trim();
    let finalConditions = conditions;
    if (trimmedCondition) {
      if (trimmedCondition.length < 3) {
        setConditionError("Must be at least 3 characters.");
        return;
      }
      if (conditions.length < 5) {
        finalConditions = [...conditions, trimmedCondition];
        setConditions(finalConditions);
        setConditionInput("");
        setConditionError(null);
      }
    }

    setIsSubmitting(true);
    try {
      let image_url = imageUrl;
      if (imageFile) {
        const res = await uploadStoreImage(user!, storeId, imageFile, "point_reward");
        image_url = res.url;
      }
      const payload: Partial<PointReward> = {
        name: values.name.trim(),
        description: values.description.trim() || undefined,
        price_cents: Math.round(parseFloat(values.price) * 100),
        points_required: parseInt(values.pointsRequired, 10),
        conditions: finalConditions,
        status: values.status,
        image_url: image_url ?? "",
      };

      if (reward) {
        await updatePointReward(user!, storeId, reward.id, payload);
      } else {
        await createPointReward(user!, storeId, payload);
      }

      await qc.invalidateQueries({ queryKey: ["point-rewards", storeId] });
      toast.success(reward ? "Reward updated." : "Reward created.");
      onClose();
    } catch (e) {
      setError("root", { message: (e as Error).message });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) tryClose();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {reward ? "Edit Point Reward" : prefill ? "Make Point Reward" : "New Point Reward"}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4 py-2">
            {/* Name */}
            <TextInput
              id="r-name"
              label="Name *"
              placeholder="Free Coffee"
              maxLength={50}
              error={errors.name?.message}
              {...register("name")}
            />

            {/* Description */}
            <Textarea
              id="r-desc"
              label="Description"
              rows={2}
              maxLength={700}
              placeholder="Optional description"
              error={errors.description?.message}
              {...register("description")}
            />

            {/* Image */}
            <ImageUpload
              label="Image"
              aspectRatio="1:1"
              currentUrl={imageUrl}
              initialFile={prefill?.imageFile}
              onFileSelect={(file) => {
                setImageFile(file);
                setImageFileDirty(true);
              }}
              onClear={() => {
                setImageFile(null);
                setImageFileDirty(true);
                setImageUrl(undefined);
              }}
              className="w-44"
            />

            {/* Price + Points */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="r-price">Retail price ($) *</Label>
                <div className="relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                    $
                  </span>
                  <Input
                    id="r-price"
                    className="pl-6"
                    type="text"
                    inputMode="decimal"
                    placeholder="5.00"
                    aria-invalid={!!errors.price}
                    {...priceField}
                    onChange={(e) => {
                      e.target.value = e.target.value
                        .replace(/[^\d.]/g, "")
                        .replace(/(\..*)\./g, "$1");
                      priceField.onChange(e);
                    }}
                  />
                </div>
                {errors.price ? (
                  <p className="text-xs text-destructive">{errors.price.message}</p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Max ${maxPriceDollars.toLocaleString()}
                  </p>
                )}
              </div>
              <div className="space-y-1">
                <Label htmlFor="r-pts">Points required *</Label>
                <Input
                  id="r-pts"
                  type="text"
                  inputMode="numeric"
                  placeholder="100"
                  aria-invalid={!!errors.pointsRequired}
                  {...pointsRequiredField}
                  onChange={(e) => {
                    pointsManuallyEdited.current = true;
                    e.target.value = e.target.value.replace(/\D/g, "");
                    pointsRequiredField.onChange(e);
                  }}
                />
                {errors.pointsRequired ? (
                  <p className="text-xs text-destructive">{errors.pointsRequired.message}</p>
                ) : (
                  (() => {
                    const d = parseFloat(priceValue);
                    return !isNaN(d) && d >= 1 ? (
                      <p className="text-xs text-muted-foreground">
                        Suggested: {Math.floor(d * 50)}
                      </p>
                    ) : null;
                  })()
                )}
              </div>
            </div>

            {/* Conditions */}
            <div className="space-y-1">
              <Label htmlFor="r-cond-input">Conditions (max 5)</Label>
              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-1">
                  <Input
                    id="r-cond-input"
                    value={conditionInput}
                    onChange={(e) => {
                      setConditionInput(e.target.value);
                      setConditionError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const trimmed = conditionInput.trim();
                        if (trimmed && trimmed.length < 3) {
                          setConditionError("Must be at least 3 characters.");
                        } else if (trimmed && conditions.length < 5) {
                          setConditions((prev) => [...prev, trimmed]);
                          setConditionInput("");
                          setConditionError(null);
                        }
                      }
                    }}
                    placeholder="e.g. One drink minimum"
                    maxLength={60}
                    disabled={conditions.length >= 5}
                  />
                  <p
                    className={`text-xs text-right ${
                      conditionInput.length > 60 ? "text-destructive" : "text-muted-foreground"
                    }`}
                  >
                    {conditionInput.length}/60
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const trimmed = conditionInput.trim();
                    if (trimmed && trimmed.length < 3) {
                      setConditionError("Must be at least 3 characters.");
                    } else if (trimmed && conditions.length < 5) {
                      setConditions((prev) => [...prev, trimmed]);
                      setConditionInput("");
                      setConditionError(null);
                    }
                  }}
                  disabled={!conditionInput.trim() || conditions.length >= 5}
                >
                  Add
                </Button>
              </div>
              {conditionError && <p className="text-xs text-destructive">{conditionError}</p>}
              {conditions.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {conditions.map((c, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center gap-1 bg-muted text-muted-foreground rounded-full px-2.5 py-0.5 text-xs"
                    >
                      {c}
                      <button
                        type="button"
                        onClick={() => setConditions((prev) => prev.filter((_, j) => j !== i))}
                        className="hover:text-foreground"
                      >
                        <X className="size-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Status */}
            <div className="space-y-1">
              <Label>Status</Label>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <SegmentedToggle
                    value={field.value}
                    onChange={field.onChange}
                    options={[
                      { value: "active", label: "Active" },
                      { value: "inactive", label: "Inactive" },
                    ]}
                  />
                )}
              />
            </div>

            {errors.root?.message && (
              <p className="text-sm text-destructive">{errors.root.message}</p>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={tryClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Spinner className="size-4 border-background" />
                ) : reward ? (
                  "Save"
                ) : (
                  "Create"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <DiscardDialog
        open={discardOpen}
        onConfirm={() => {
          setDiscardOpen(false);
          onClose();
        }}
        onCancel={() => setDiscardOpen(false)}
      />
    </>
  );
}
